import React, { useState, useEffect } from "react";
import { getAuthInstance } from "./firebase";
import {
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { createFreeSubscription, createOnboarding } from "./serverComm";
import toast from "react-hot-toast";
import { useTimer } from "./hooks/useTimer";
import { Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  signUpSchema,
  signInSchema,
  forgotPasswordSchema,
} from "./utils/validationSchema";
import ErrorText from "./components/form/ErrorText";
import { trackSignupConversion } from "./utils/analytics";

const SignIn = ({ onClose, isSignUp = false }) => {
  const auth = getAuthInstance();
  const [screen, setScreen] = useState(isSignUp ? "signUp" : "signIn");
  const { time, start, isRunning } = useTimer(60);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const signUpForm = useForm({
    resolver: zodResolver(signUpSchema),
    mode: "onChange",
  });

  const signInForm = useForm({
    resolver: zodResolver(signInSchema),
    mode: "onChange",
  });

  const forgotPasswordForm = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onChange",
  });

  const handleEmailSignIn = async (data) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      const user = userCredential.user;

      if (!user.emailVerified) {
        await sendEmailVerification(userCredential.user);
        setVerificationData({ email: data.email, password: data.password });
        await auth.signOut(); // sign them out immediately
        setScreen("verificationLinkSend");
        start();
        return;
      }

      // Create free subscription and onboarding if not already created
      await createFreeSubscription();
      const onboardingResponse = await createOnboarding();

      if (!onboardingResponse?.onboarding) {
        toast.error("Something went wrong during setup. Please try again.");
        return;
      }

      onClose();
      // New users go to onboarding, returning users go to workspaces
      if (!onboardingResponse.onboarding.isCompleted) {
        window.location.href = `/onboarding?step=${onboardingResponse.onboarding.currentStep || 0}`;
      } else {
        window.location.href = `/workspaces`;
      }
    } catch (error) {
      if (error.message === "Firebase: Error (auth/invalid-credential).") {
        toast.error("Invalid email or password");
      } else {
        toast.error("Error signing in with email and password");
      }
    }
  };

  const handleEmailSignUp = async (data) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      const user = userCredential.user;
      // Use email prefix as displayName
      const displayName = data.email.split('@')[0];
      await updateProfile(user, {
        displayName,
      });
      // Send verification email
      await sendEmailVerification(userCredential.user);
      setVerificationData({ email: data.email, password: data.password });
      setScreen("verificationLinkSend");
      start();

      // Create free subscription and onboarding
      await createFreeSubscription();
      await createOnboarding();

      // Track signup conversion (Google Ads + GA4)
      trackSignupConversion('email');

      // Sign user out immediately so they can't log in until verified
      await auth.signOut();
    } catch (error) {
      if (error.message === "Firebase: Error (auth/invalid-credential).") {
        toast.error("Invalid email or password");
      } else if (
        error.message === "Firebase: Error (auth/email-already-in-use)."
      ) {
        toast.error("Email already in use");
      } else {
        toast.error("Error signing up with email and password");
      }
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      await createFreeSubscription();
      const onboardingResponse = await createOnboarding();
      
      // Track signup conversion for new users only (Google Ads + GA4)
      if (!onboardingResponse.onboarding.isCompleted) {
        trackSignupConversion('google');
      }
      
      onClose();
      // New users go to onboarding, returning users go to workspaces
      if (!onboardingResponse.onboarding.isCompleted) {
        window.location.href = `/onboarding?step=${onboardingResponse.onboarding.currentStep || 0}`;
      } else {
        window.location.href = `/workspaces`;
      }
    } catch (error) {
      toast.error("Error signing in with Google");
      console.error("Error signing in with Google", error);
    }
  };

  const handleForgetPassword = async (data) => {
    try {
      await sendPasswordResetEmail(auth, data.email);
      setScreen("resetLinkSend");
      start();
    } catch (error) {
      toast.error("Error sending reset password email");
    }
  };

  const handleResendVerificationEmail = async () => {
    if (!verificationData) {
      throw new Error("No verification data found");
    }
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        verificationData.email,
        verificationData.password
      );
      const user = userCredential.user;

      if (!user.emailVerified) {
        await sendEmailVerification(userCredential.user);
        await auth.signOut(); // sign them out immediately
        setScreen("verificationLinkSend");
        start();
        return;
      } else {
        setScreen("signIn");
      }
    } catch (error) {
      toast.error("Error re-sending verification email");
    }
  };

  const handleResendResetPasswordEmail = async () => {
    try {
      await sendPasswordResetEmail(auth, forgotPasswordForm.getValues("email"));
      setScreen("resetLinkSend");
      start();
    } catch (error) {
      toast.error("Error re-sending reset password email");
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      // ... your auth state change logic
    });

    return () => unsubscribe();
  }, []);

  const content = (
    <>
      {screen === "signUp" ? (
        <div className="relative bg-transparent py-6 px-4 sm:py-8 sm:px-6 md:py-10 md:px-8 w-full max-w-[90vw] sm:max-w-[480px] mx-auto">

          <div className="text-center mb-6 sm:mb-8">
            <div className="mb-4 flex justify-center">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2Flogo_full.svg?alt=media&token=e0997512-c75e-43bd-83d9-209d1d29f353" 
                alt="PollBolt Logo" 
                className="h-12 sm:h-12 w-auto"
              />
            </div>
            <p className="text-base sm:text-base text-gray-600 leading-relaxed font-inter">Build a form in minutes. Share it anywhere. Track results in real time.</p>
          </div>

          <GoogleButton handleGoogleSignIn={handleGoogleSignIn} />

          <div className="relative mb-4 sm:mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm sm:text-sm">
              <span className="px-3 sm:px-4 bg-white text-gray-500 font-medium font-inter">
                OR
              </span>
            </div>
          </div>

          <button
            onClick={() => setScreen("signUpEmail")}
            className="w-full bg-black text-white py-4 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:bg-black/90 transition-all font-semibold text-base sm:text-base font-inter flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Sign up with email
          </button>

          <div className="mt-4 sm:mt-6 text-center">
            <button
              onClick={() => setScreen("signIn")}
              className="text-gray-600 hover:text-black transition-colors text-base sm:text-base font-medium font-inter"
            >
              Already have an account? Sign In
            </button>
          </div>
        </div>
      ) : screen === "signUpEmail" ? (
        <div className="relative bg-transparent py-6 px-4 sm:py-8 sm:px-6 md:py-10 md:px-8 w-full max-w-[90vw] sm:max-w-[480px] mx-auto">

          <div className="text-center mb-6 sm:mb-8">
            <div className="mb-4 flex justify-center">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2Flogo_full.svg?alt=media&token=e0997512-c75e-43bd-83d9-209d1d29f353" 
                alt="PollBolt Logo" 
                className="h-12 sm:h-12 w-auto"
              />
            </div>
            <p className="text-base sm:text-base text-gray-600 leading-relaxed font-inter">Build a form in minutes. Share it anywhere. Track results in real time.</p>
          </div>

          <form
            onSubmit={signUpForm.handleSubmit(handleEmailSignUp)}
            className="space-y-4 sm:space-y-4"
          >
            <div>
              <input
                type="email"
                {...signUpForm.register("email")}
                placeholder="Email address"
                className={`w-full p-4 sm:p-4 text-base sm:text-base border ${
                  signUpForm.formState.errors.email
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFFD63] focus:border-transparent transition-all font-medium`}
              />
              {signUpForm.formState.errors.email && (
                <p className="mt-2 text-sm sm:text-sm text-red-500 font-medium">
                  {signUpForm.formState.errors.email.message}
                </p>
              )}
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...signUpForm.register("password")}
                placeholder="Password"
                className={`w-full p-4 sm:p-4 pr-12 sm:pr-12 text-base sm:text-base border ${
                  signUpForm.formState.errors.password
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFFD63] focus:border-transparent transition-all font-medium`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none p-1"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
              {signUpForm.formState.errors.password && (
                <ErrorText>
                  {signUpForm.formState.errors.password.message}
                </ErrorText>
              )}
            </div>
            
            <div className="flex items-start gap-3 mt-4">
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-[#FFFD63] focus:ring-2 cursor-pointer"
              />
              <label htmlFor="terms-checkbox" className="text-xs sm:text-sm text-gray-500 leading-relaxed cursor-pointer">
                I agree to Pollbolt's{" "}
                <a
                  href="https://www.pollbolt.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:underline font-medium"
                >
                  Terms of Service
                </a>
                ,{" "}
                <a
                  href="https://www.pollbolt.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:underline font-medium"
                >
                  Privacy Policy
                </a>
                , and{" "}
                <a
                  href="https://www.pollbolt.com/data-processing-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:underline font-medium"
                >
                  Data Processing Agreement
                </a>
                .
              </label>
            </div>

            <button
              type="submit"
              disabled={!termsAccepted}
              className={`w-full py-4 sm:py-4 px-4 rounded-lg sm:rounded-xl transition-all font-semibold text-base sm:text-base font-inter bg-black text-white ${
                termsAccepted 
                  ? "hover:bg-black/90" 
                  : "cursor-not-allowed"
              }`}
            >
              Create my free account
            </button>
          </form>

          <div className="mt-4 sm:mt-6 text-center">
            <button
              onClick={() => {
                setScreen("signUp");
                setTermsAccepted(false);
                signUpForm.reset();
              }}
              className="text-gray-600 hover:text-black transition-colors text-base sm:text-base font-medium font-inter"
            >
              ← Back to sign up options
            </button>
          </div>
        </div>
      ) : screen === "signIn" ? (
        <div className="relative bg-transparent py-6 px-4 sm:py-8 sm:px-6 md:py-10 md:px-8 w-full max-w-[90vw] sm:max-w-[480px] mx-auto">

          <div className="text-center mb-6 sm:mb-8">
            <div className="mb-4 flex justify-center">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2Flogo_full.svg?alt=media&token=e0997512-c75e-43bd-83d9-209d1d29f353" 
                alt="PollBolt Logo" 
                className="h-12 sm:h-12 w-auto"
              />
            </div>
            <p className="text-base sm:text-base text-gray-600 leading-relaxed font-inter">Sign in to continue to PollBolt</p>
          </div>
          <GoogleButton handleGoogleSignIn={handleGoogleSignIn} />

          <div className="relative mb-4 sm:mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm sm:text-sm">
              <span className="px-3 sm:px-4 bg-white text-gray-500 font-medium font-inter">
                or continue with email
              </span>
            </div>
          </div>

          <form
            onSubmit={signInForm.handleSubmit(handleEmailSignIn)}
            className="space-y-4 sm:space-y-4"
          >
            <div>
              <input
                type="email"
                {...signInForm.register("email")}
                placeholder="Email address"
                className={`w-full p-4 sm:p-4 text-base sm:text-base border ${
                  signInForm.formState.errors.email
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFFD63] focus:border-transparent transition-all font-medium`}
              />
              {signInForm.formState.errors.email && (
                <ErrorText>
                  {signInForm.formState.errors.email.message}
                </ErrorText>
              )}
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...signInForm.register("password")}
                placeholder="Password"
                className={`w-full p-4 sm:p-4 pr-12 sm:pr-12 text-base sm:text-base border ${
                  signInForm.formState.errors.password
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFFD63] focus:border-transparent transition-all font-medium`}
              />

              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none p-1"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
              {signInForm.formState.errors.password && (
                <ErrorText>
                  {signInForm.formState.errors.password.message}
                </ErrorText>
              )}
            </div>
            <div
              className="text-sm sm:text-sm text-end mt-2 mb-3 sm:mb-4 text-gray-600 hover:text-black transition-colors cursor-pointer font-medium font-inter"
              onClick={() => setScreen("forgetPassword")}
            >
              Forget password ?
            </div>

            <button
              type="submit"
              className="w-full bg-black text-white py-4 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:bg-black/90 transition-all font-semibold text-base sm:text-base font-inter"
            >
              Sign In
            </button>
          </form>

          <div className="mt-4 sm:mt-6 text-center">
            <button
              onClick={() => setScreen("signUp")}
              className="text-gray-600 hover:text-black transition-colors text-base sm:text-base font-medium font-inter"
            >
              Don't have an account? Sign Up
            </button>
          </div>
        </div>
      ) : screen === "forgetPassword" ? (
        <div className="relative bg-transparent py-6 px-4 sm:py-8 sm:px-6 md:py-10 md:px-8 w-full max-w-[90vw] sm:max-w-[480px] mx-auto">

          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3 font-['Inter'] leading-tight">
              Forget Password
            </h2>
            <p className="text-base sm:text-base text-gray-600 leading-relaxed font-inter">
              Enter your email to reset your password.
            </p>
          </div>
          <form
            onSubmit={forgotPasswordForm.handleSubmit(handleForgetPassword)}
            className="space-y-4 sm:space-y-4"
          >
            <div>
              <input
                type="email"
                {...forgotPasswordForm.register("email")}
                placeholder="Email address"
                className={`w-full p-4 sm:p-4 text-base sm:text-base border ${
                  forgotPasswordForm.formState.errors.email
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFFD63] focus:border-transparent transition-all font-medium`}
              />
              {forgotPasswordForm.formState.errors.email && (
                <p className="mt-2 text-sm sm:text-sm text-red-500 font-medium">
                  {forgotPasswordForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-black text-white py-4 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:bg-black/90 transition-all font-semibold text-base sm:text-base font-inter"
            >
              Get Reset Link
            </button>
          </form>

          <div className="mt-4 sm:mt-6 text-center">
            <button
              onClick={() => setScreen("signIn")}
              className="text-gray-600 hover:text-black transition-colors text-base sm:text-base font-medium font-inter"
            >
              Don't have to reset password? Sign In
            </button>
          </div>
        </div>
      ) : screen === "verificationLinkSend" ? (
        <div className="relative bg-transparent py-6 px-4 sm:py-8 sm:px-6 md:py-10 md:px-8 w-full max-w-[90vw] sm:max-w-[480px] mx-auto">

          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3 font-['Inter'] leading-tight">
              Email Verification
            </h2>
            <p className="text-base sm:text-base text-gray-600 leading-relaxed font-inter">
              Please verify your email to continue to PollBolt.
            </p>
          </div>
          <div className="bg-[#E0F0E4] p-4 sm:p-4 text-sm sm:text-sm rounded-lg sm:rounded-xl mb-4 sm:mb-6 text-center text-[#377E36] font-medium font-inter">
            Verification email sent
            {verificationData?.email ? ` on ${verificationData.email}` : ""}
            .<br /> Please check your inbox.
          </div>
          <div className="text-center">
            {isRunning ? (
              <p className="text-gray-500 text-xs font-inter">Resend in {time} seconds</p>
            ) : (
              <button
                onClick={handleResendVerificationEmail}
                className="text-gray-600 hover:text-black text-xs transition-colors font-inter"
              >
                Resend verification email
              </button>
            )}
          </div>
          <div className="mt-4 sm:mt-6 text-center">
            <button
              onClick={() => setScreen("signIn")}
              className="text-gray-600 hover:text-black transition-colors text-base sm:text-base font-medium font-inter"
            >
              Verified? Sign In
            </button>
          </div>
        </div>
      ) : screen === "resetLinkSend" ? (
        <div className="relative bg-transparent py-6 px-4 sm:py-8 sm:px-6 md:py-10 md:px-8 w-full max-w-[90vw] sm:max-w-[480px] mx-auto">

          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3 font-['Inter'] leading-tight">
              Reset Password
            </h2>
            <p className="text-base sm:text-base text-gray-600 leading-relaxed font-inter">Reset password link is sent.</p>
          </div>
          <div className="bg-[#E0F0E4] p-4 sm:p-4 text-sm sm:text-sm rounded-lg sm:rounded-xl mb-4 sm:mb-6 text-center text-[#377E36] font-medium font-inter">
            Reset password link is sent
            {forgotPasswordForm.getValues("email")
              ? ` on ${forgotPasswordForm.getValues("email")}`
              : ""}
            .<br /> Please check your inbox.
          </div>
          <div className="text-center">
            {isRunning ? (
              <p className="text-gray-500 text-xs font-inter">Resend in {time} seconds</p>
            ) : (
              <button
                onClick={handleResendResetPasswordEmail}
                className="text-gray-600 hover:text-black text-xs transition-colors font-inter"
              >
                Resend reset password email
              </button>
            )}
          </div>
          <div className="mt-4 sm:mt-6 text-center">
            <button
              onClick={() => setScreen("signIn")}
              className="text-gray-600 hover:text-black transition-colors text-base sm:text-base font-medium font-inter"
            >
              Don't want to reset password? Sign In
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  return content;
};

export default SignIn;

const GoogleButton = ({ handleGoogleSignIn }) => {
  return (
    <button
      onClick={handleGoogleSignIn}
      className="w-full bg-white border border-gray-200 text-gray-700 py-4 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors mb-4 sm:mb-6 flex items-center justify-center gap-2 sm:gap-3 font-semibold text-base sm:text-base font-inter"
    >
      <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
        <path fill="none" d="M1 1h22v22H1z" />
      </svg>
      Continue with Google
    </button>
  );
};
